import { useEffect, useState } from "react";
import { I } from "@/components/Icon";
import { relativeTime } from "@/lib/overview-helpers";

/** Shows the last-updated timestamp + a small green "live" pulse, with
 *  a refresh action on click. Replaces the old plain icon button — the
 *  user couldn't tell whether a click had actually done anything,
 *  because data updated in the background without any visual cue. */
export function RefreshControl({
  lastUpdatedAt,
  busy,
  onRefresh,
}: {
  lastUpdatedAt: number;
  busy: boolean;
  onRefresh: () => void | Promise<void>;
}) {
  // Force a re-render every 20s so "2 min ago" stays accurate without
  // touching React Query. The underlying timestamp itself doesn't
  // change; we just want the label to tick.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((x) => x + 1), 20_000);
    return () => clearInterval(id);
  }, []);

  const label =
    !lastUpdatedAt || lastUpdatedAt === 0
      ? "Loading…"
      : relativeTime(lastUpdatedAt);

  return (
    <button
      type="button"
      className="refresh-ctl"
      onClick={() => void onRefresh()}
      disabled={busy}
      title="Refresh the dashboard"
      style={{
        // Height + radius + shadow are owned by .ov-filter-row CSS so
        // every picker in that row renders with identical silhouette.
        // Only in-pill styling lives here.
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        padding: "0 16px 0 14px",
        border: "1px solid var(--c-border)",
        background: "var(--c-surface)",
        color: "var(--c-text)",
        fontSize: 13,
        cursor: busy ? "wait" : "pointer",
        lineHeight: 1,
      }}
    >
      <span
        aria-hidden
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: "#22c55e",
          boxShadow: "0 0 0 0 rgba(34,197,94,0.6)",
          animation: "refresh-pulse 1.6s ease-out infinite",
          flex: "none",
        }}
      />
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 16,
          height: 16,
          opacity: 0.8,
          animation: busy ? "refresh-spin 0.8s linear infinite" : undefined,
        }}
      >
        {I.refresh}
      </span>
      <span
        style={{
          display: "inline-flex",
          flexDirection: "column",
          alignItems: "flex-start",
          lineHeight: 1.2,
          whiteSpace: "nowrap",
        }}
      >
        <span
          style={{
            color: "var(--c-text-3)",
            fontSize: 11.5,
            fontWeight: 500,
            textTransform: "uppercase",
            letterSpacing: 0.04,
          }}
        >
          Updated
        </span>
        <span style={{ fontWeight: 600, fontSize: 13 }}>{label}</span>
      </span>
      <style>{`
        @keyframes refresh-pulse {
          0%   { box-shadow: 0 0 0 0   rgba(34,197,94,0.55); }
          70%  { box-shadow: 0 0 0 8px rgba(34,197,94,0);     }
          100% { box-shadow: 0 0 0 0   rgba(34,197,94,0);     }
        }
        @keyframes refresh-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        .refresh-ctl:hover:not(:disabled) {
          border-color: var(--c-border-strong);
          background: var(--c-surface-sunk);
        }
      `}</style>
    </button>
  );
}
